// src/preview/Player.tsx
// The draft preview: a canvas that plays a spec in real time (Stage draws
// each frame), with play/pause, scrubbing, music through Web Audio, and
// "Download draft", which records the canvas and music with MediaRecorder
// in real time. Drafts are for checking and sharing; finals come from the
// worker.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Stage, StageError, audioContext, videoAt, type AssetLookup, type StageNote } from './stage';
import { audioGainAt } from './timeline';
import type { Json } from './spec';
import './Player.css';

/** Rebuilding waits this long after the last edit, so typing doesn't rebuild on every key. */
const REBUILD_DELAY_MS = 300;

interface Props {
  /** The spec to show, or null while the JSON doesn't parse. */
  spec:   Json | null;
  lookup: AssetLookup;
  /** File name for a downloaded draft (without extension). */
  name:   string;
  /**
   * Jump to `t` when `nonce` changes, and start playing if `play` (the
   * editor's "play from here"). `ifPaused` jumps only while paused (moving
   * into a beat in the editor shows it, but doesn't interrupt playback).
   */
  seek?:  { t: number; nonce: number; play?: boolean; ifPaused?: boolean };
  /** Called when the beat under the playhead changes (-1 = the end card). */
  onBeat?: (beat: number) => void;
}

/** Container types to record in, best first. MP4 where the browser can (Safari, recent Chrome), else WebM. */
const RECORD_TYPES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function recordType(): string | null {
  if (typeof MediaRecorder === 'undefined' || !('captureStream' in HTMLCanvasElement.prototype)) return null;
  return RECORD_TYPES.find(t => MediaRecorder.isTypeSupported(t)) ?? null;
}

function fmtTime(t: number): string {
  const s = Math.max(0, t);
  return `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`;
}

function beatAt(stage: Stage, t: number): number {
  const { beatStarts, beatsTotal } = stage.tl;
  if (t >= beatsTotal && stage.tl.hasEnd) return -1;
  let i = 0;
  while (i + 1 < beatStarts.length && beatStarts[i + 1] <= t) i++;
  return i;
}

export function Player({ spec, lookup, name, seek, onBeat }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stage, setStage] = useState<Stage | null>(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [recording, setRecording] = useState<{ progress: number } | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);

  // Mutable playback state the animation loop reads without re-rendering
  const live = useRef({
    t: 0, playing: false, clockStart: 0, startT: 0, raf: 0,
    /** Bumped by every play() and pause(): a play() still waiting (audio resume, video seek) gives up if it changed. */
    gen: 0,
    /** While a draft records, the stage stays put: a rebuild waits here and is shown when recording ends. */
    recording: false,
    pending: null as Stage | null,
    source: null as AudioBufferSourceNode | null,
    recordDest: null as MediaStreamAudioDestinationNode | null,
    onEnd: null as (() => void) | null,
    beat: 0,
  });
  const stageRef = useRef<Stage | null>(null);
  const onBeatRef = useRef(onBeat);
  useEffect(() => { onBeatRef.current = onBeat; }, [onBeat]);

  const draw = useCallback((at: number) => {
    const s = stageRef.current, canvas = canvasRef.current;
    if (!s || !canvas) return;
    if (canvas.width !== s.w || canvas.height !== s.h) {
      canvas.width = s.w;
      canvas.height = s.h;
    }
    s.draw(canvas.getContext('2d')!, at);
    const beat = beatAt(s, at);
    if (beat !== live.current.beat) {
      live.current.beat = beat;
      onBeatRef.current?.(beat);
    }
  }, []);

  const stopAudio = useCallback(() => {
    const src = live.current.source;
    live.current.source = null;
    if (src) { try { src.stop(); } catch { /* already stopped */ } src.disconnect(); }
  }, []);

  const pause = useCallback(() => {
    const l = live.current;
    l.gen++; // also cancels a play() that hasn't started yet
    if (!l.playing) return;
    l.playing = false;
    cancelAnimationFrame(l.raf);
    stopAudio();
    stageRef.current?.video?.pause();
    setPlaying(false);
    setT(l.t);
  }, [stopAudio]);

  /** Starts the music at timeline time t0, with make_video's fades. */
  const startAudio = useCallback((s: Stage, t0: number) => {
    const audio = s.audio;
    if (!audio) return;
    const ctx = audioContext();
    const offset = audio.start + t0;
    const remaining = s.total - t0;
    if (offset >= audio.buffer.duration || remaining <= 0) return; // past the end of the track: silence, like apad
    const src = ctx.createBufferSource();
    src.buffer = audio.buffer;
    const gain = ctx.createGain();
    // The fade curve, sampled 50 times a second and interpolated between (both fades are linear)
    const n = Math.max(2, Math.ceil(remaining * 50));
    const curve = new Float32Array(n);
    for (let i = 0; i < n; i++) curve[i] = audioGainAt(audio, s.total, t0 + remaining * i / (n - 1));
    const at = ctx.currentTime;
    gain.gain.setValueCurveAtTime(curve, at, remaining);
    src.connect(gain).connect(ctx.destination);
    if (live.current.recordDest) gain.connect(live.current.recordDest);
    src.start(at, offset, remaining);
    live.current.source = src;
  }, []);

  const play = useCallback(async (from?: number) => {
    const s = stageRef.current;
    if (!s) return;
    const l = live.current;
    if (l.playing) pause();
    const gen = ++l.gen;
    let t0 = from ?? l.t;
    if (t0 >= s.total - 1e-3) t0 = 0;
    await audioContext().resume(); // browsers start audio suspended until a click
    const video = s.video;
    if (video) await videoAt(video, t0);
    // Another play() or a pause() arrived while waiting: that one wins, so two never both start music
    if (gen !== l.gen || stageRef.current !== s) return;
    if (video) void video.play().catch(() => { /* drawn from wherever it is */ });
    stopAudio();
    l.playing = true;
    l.startT = t0;
    l.clockStart = performance.now();
    startAudio(s, t0);
    setPlaying(true);
    const tick = () => {
      if (!l.playing) return;
      const now = l.startT + (performance.now() - l.clockStart) / 1000;
      l.t = Math.min(now, s.total);
      // keep a looping background video within a few frames of the clock
      if (video && video.duration > 0 && Math.abs(video.currentTime - l.t % video.duration) > 0.25
          && Math.abs(video.currentTime - l.t % video.duration) < video.duration - 0.25) {
        video.currentTime = l.t % video.duration;
      }
      draw(l.t);
      if (now >= s.total) {
        pause();
        l.onEnd?.();
        return;
      }
      if (Math.round(now * 10) !== Math.round((now - 0.016) * 10)) setT(l.t); // the scrubber moves ~10 times a second
      l.raf = requestAnimationFrame(tick);
    };
    l.raf = requestAnimationFrame(tick);
  }, [draw, pause, startAudio, stopAudio]);

  const seekTo = useCallback(async (to: number) => {
    const s = stageRef.current;
    if (!s) return;
    const at = Math.max(0, Math.min(to, s.total));
    const wasPlaying = live.current.playing;
    if (wasPlaying) pause();
    live.current.t = at;
    setT(at);
    if (s.video) await videoAt(s.video, at);
    draw(at);
    if (wasPlaying) void play(at);
  }, [draw, pause, play]);

  /** Shows a newly built stage, carrying on playing from the same time if it was. */
  const showStage = useCallback((s: Stage) => {
    const wasPlaying = live.current.playing;
    if (wasPlaying) pause();
    stageRef.current = s;
    live.current.t = Math.min(live.current.t, s.total);
    setStage(s);
    setError(null);
    setT(live.current.t);
    const at = live.current.t;
    if (s.video) void videoAt(s.video, at).then(() => draw(at));
    else draw(at);
    if (wasPlaying) void play(at);
  }, [draw, pause, play]);

  // Rebuild the stage shortly after the spec (or the library) changes
  useEffect(() => {
    if (!spec) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setBuilding(true);
      Stage.build(spec, lookup).then(s => {
        if (cancelled) return;
        if (live.current.recording) live.current.pending = s; // shown once the draft is recorded
        else showStage(s);
      }, err => {
        if (cancelled) return;
        setError(err instanceof StageError ? err.message : `The preview failed: ${err instanceof Error ? err.message : err}`);
      }).finally(() => { if (!cancelled) setBuilding(false); });
    }, REBUILD_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [spec, lookup, showStage]);

  // The editor asked to jump somewhere (not while a draft records: that would cut it short)
  useEffect(() => {
    if (!seek || live.current.recording) return;
    if (seek.ifPaused && live.current.playing) return;
    if (seek.play) void play(seek.t);
    else void seekTo(seek.t);
  }, [seek, seekTo, play]);

  // Stop everything on the way out
  useEffect(() => () => {
    live.current.playing = false;
    cancelAnimationFrame(live.current.raf);
    stopAudio();
    stageRef.current?.video?.pause();
  }, [stopAudio]);

  const mime = recordType();

  async function downloadDraft() {
    const s = stageRef.current, canvas = canvasRef.current;
    if (!s || !canvas || !mime) return;
    setRecordError(null);
    const stream = canvas.captureStream(s.fps);
    const l = live.current;
    if (s.audio) {
      l.recordDest = audioContext().createMediaStreamDestination();
      stream.addTrack(l.recordDest.stream.getAudioTracks()[0]);
    }
    const chunks: Blob[] = [];
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 });
    recorder.ondataavailable = e => { if (e.data.size) chunks.push(e.data); };
    const stopped = new Promise<void>(resolve => { recorder.onstop = () => resolve(); });
    let finished = false;
    l.onEnd = () => { finished = true; };
    l.recording = true;
    setRecording({ progress: 0 });
    const progressTimer = setInterval(() => setRecording({ progress: l.t / s.total }), 250);
    try {
      draw(0);
      recorder.start(1000);
      await play(0);
      // wait for the end (or Stop)
      await new Promise<void>(resolve => {
        const check = () => (live.current.playing ? setTimeout(check, 100) : resolve());
        check();
      });
      draw(s.total); // make sure the last frame is in the stream
      await new Promise(r => setTimeout(r, 200));
      recorder.stop();
      await stopped;
      if (!finished) return; // stopped early: throw it away
      const type = mime.split(';')[0];
      const url = URL.createObjectURL(new Blob(chunks, { type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name || 'video'} (draft).${type === 'video/mp4' ? 'mp4' : 'webm'}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setRecordError(`Recording failed: ${err instanceof Error ? err.message : err}`);
      if (recorder.state !== 'inactive') recorder.stop();
    } finally {
      clearInterval(progressTimer);
      l.onEnd = null;
      l.recordDest?.disconnect();
      l.recordDest = null;
      stream.getTracks().forEach(track => track.stop());
      l.recording = false;
      setRecording(null);
      // Edits made while recording
      const pending = l.pending;
      l.pending = null;
      if (pending) showStage(pending);
    }
  }

  const notes = dedupe(stage?.notes ?? []);
  const total = stage?.total ?? 0;
  const fps = stage?.fps ?? 30;

  return (
    <div className="player">
      <div className="player-screen" style={stage ? { aspectRatio: `${stage.w} / ${stage.h}` } : undefined}>
        <canvas ref={canvasRef} className="player-canvas" onClick={() => { if (!recording) { if (playing) pause(); else void play(); } }} />
        {!stage && !error && <div className="player-overlay">{spec ? 'Loading preview…' : 'Fix the JSON to see the preview.'}</div>}
        {error && <div className="player-overlay player-overlay-error">{error}</div>}
        {building && stage && <div className="player-building" aria-live="polite">Updating…</div>}
      </div>

      <div className="player-controls">
        <button type="button" className="player-play" disabled={!stage || !!recording}
          onClick={() => (playing ? pause() : void play())} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? '❚❚' : '▶'}
        </button>
        <input type="range" className="player-scrub" min={0} max={total || 1} step={1 / fps} value={Math.min(t, total)}
          disabled={!stage || !!recording} onChange={e => void seekTo(Number(e.target.value))} aria-label="Position" />
        <span className="player-time">{fmtTime(t)} / {fmtTime(total)}</span>
      </div>

      <div className="player-actions">
        {recording ? (
          <>
            <span className="player-recording">Recording draft… {Math.round(recording.progress * 100)}%. Keep this tab open; edits show when it's done.</span>
            <button type="button" className="projects-secondary" onClick={pause}>Stop</button>
          </>
        ) : (
          <button type="button" className="projects-secondary" disabled={!stage || !mime || !!error}
            title={mime ? 'Plays the video once and saves what you see and hear (draft quality)' : 'This browser can\'t record video'}
            onClick={() => void downloadDraft()}>
            Download draft
          </button>
        )}
        <span className="projects-sub player-hint">Draft preview. The final render is sharper and sets the volume.</span>
      </div>
      {recordError && <p className="page-error-text">{recordError}</p>}

      {notes.length > 0 && (
        <ul className="player-notes">
          {notes.map(n => (
            <li key={n.level + n.message} className={`player-note player-note-${n.level}`}>
              {n.level === 'approx' && <strong>Preview approximates this: </strong>}
              {n.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function dedupe(notes: StageNote[]): StageNote[] {
  const seen = new Set<string>();
  return notes.filter(n => !seen.has(n.level + n.message) && seen.add(n.level + n.message));
}
